import type {DoppeActionType} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {
    doppeActionTypeCreateSettingsResolvingFunction,
    doppeActionTypeDefineMetadata
} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {HideableValue} from '@wix/devzai-utils-common';
import {arrayRemoveNullValues, hideableValueCreateHidden} from '@wix/devzai-utils-common';
import type {RichTextContent} from '@wix/devzai-utils-common';
import type {BymoPagePropsContentSettingsMetadata} from '../../client-server-common';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    DoppeExternalFormProvider,
    doppeExternalFormProviderResolveBusinessEntityReference,
} from '../../client-server-common';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-content-sections-settings';

export namespace DoppeActionTypeExternalForm {

    export interface ActionSettings extends DoppeActionCtaButtonsSettings, DoppeActionContentSectionsSettings {
        formProvider: DoppeExternalFormProvider | null;
        formProviderId: string | null;

        title: HideableValue<string>;
        description: HideableValue<RichTextContent>;
    }
}

export const doppeActionTypeExternalFormActionContentSettingsMetadata = {
    title: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
    description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
} satisfies BymoPagePropsContentSettingsMetadata<DoppeActionTypeExternalForm.ActionSettings>

export const doppeActionTypeExternalFormSettingsMetadata = {
    contentSettingsMetadata: doppeActionTypeExternalFormActionContentSettingsMetadata,
    supportsActionCtaButtons: {
        customCtaButtonTypes: {}
    },
    supportsContentSections: {
        customContentSectionsTypes: {}
    },
    hideableContentSettingsNames: {
        description: true,
        title: true
    }
} as DoppeActionType.SettingsMetadata<DoppeActionTypeExternalForm.ActionSettings>

export const doppeActionTypeExternalForm = doppeActionTypeDefineMetadata<DoppeActionTypeExternalForm.ActionSettings>({
    id: 'co.hopp.action/wix-form',
    resolveActionSettings: doppeActionTypeCreateSettingsResolvingFunction<DoppeActionTypeExternalForm.ActionSettings>({
        formProvider: null,
        formProviderId: null,
        title: hideableValueCreateHidden(''),
        description: hideableValueCreateHidden(''),
        ...doppeActionContentSectionSettingsDefaultValuesMigration({}),
        ...doppeActionCtaButtonsSettingsDefaultValuesMigration({}, false),
    }),
    actionSettingsMetadata: doppeActionTypeExternalFormSettingsMetadata,
    actionListItemSettingsMetadata: false,
    isWidgetOpener: true,
    isFormAction: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    canRenderInPage: true,
    hasDisplaySettings: true,
    notSupportsSearch: true,
    lockIsNotSupported: true,
    resolveUsedBusinessEntities: actionSettings => {
        return arrayRemoveNullValues([
            actionSettings.formProvider !== null && actionSettings.formProviderId !== null ?
                doppeExternalFormProviderResolveBusinessEntityReference(
                    actionSettings.formProvider,
                    actionSettings.formProviderId
                ) :
                null
        ]);
    },
    resolveUsedMedia: _actionSettings => {
        return [];
    },
});
